import { FC, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Clock,
  User,
  Phone,
  Mail,
  MapPin,
  FileText,
  ChevronDown,
  ChevronUp,
  Calendar,
} from 'lucide-react';
import { cn } from '../../../utils/cn';
import { formatCurrency } from '../../../utils/currency';
import type { TherapistAppointment } from '../../../types/therapist-dashboard.types';

interface UpcomingBookingsProps {
  appointments: TherapistAppointment[];
  isLoading?: boolean;
  onUpdateStatus?: (appointmentId: string, status: 'completed' | 'no_show' | 'cancelled') => void;
}

interface BookingCardProps {
  appointment: TherapistAppointment;
  onUpdateStatus?: (appointmentId: string, status: 'completed' | 'no_show' | 'cancelled') => void;
}

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}

function formatTime(isoString: string): string {
  const date = new Date(isoString);
  return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

const defaultStatusStyle = {
  bg: 'bg-gray-100',
  text: 'text-gray-700',
  border: 'border-gray-200',
  label: 'PENDING',
};

const statusStyles: Record<string, { bg: string; text: string; border: string; label: string }> = {
  confirmed: {
    bg: 'bg-blue-100',
    text: 'text-blue-700',
    border: 'border-blue-200',
    label: 'CONFIRMED',
  },
  completed: {
    bg: 'bg-green-100',
    text: 'text-green-700',
    border: 'border-green-200',
    label: 'COMPLETED',
  },
  cancelled: {
    bg: 'bg-red-100',
    text: 'text-red-700',
    border: 'border-red-200',
    label: 'CANCELLED',
  },
  no_show: {
    bg: 'bg-yellow-100',
    text: 'text-yellow-700',
    border: 'border-yellow-200',
    label: 'NO SHOW',
  },
  pending: defaultStatusStyle,
};

function getStatusConfig(status: string) {
  return statusStyles[status] || defaultStatusStyle;
}

function BookingCard({ appointment, onUpdateStatus }: BookingCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const startTime = formatTime(appointment.startTime);
  const endTime = formatTime(appointment.endTime);
  const dateDisplay = formatDate(appointment.startTime);
  const config = getStatusConfig(appointment.status);

  const isUpcoming = new Date(appointment.startTime) > new Date();
  const canUpdateStatus = appointment.status === 'confirmed' && !isUpcoming;

  return (
    <div className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700 hover:shadow-md transition-shadow overflow-hidden">
      {/* Main Content */}
      <div className="p-4">
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center space-x-3">
            {appointment.clientAvatar ? (
              <img
                src={appointment.clientAvatar}
                alt={appointment.clientName}
                className="w-12 h-12 rounded-full object-cover"
              />
            ) : (
              <div className="w-12 h-12 rounded-full bg-gray-200 dark:bg-slate-700 flex items-center justify-center">
                <User className="w-6 h-6 text-gray-500 dark:text-slate-400" />
              </div>
            )}
            <div>
              <div className="flex items-center space-x-2">
                <h4 className="font-semibold text-gray-900 dark:text-slate-100">{appointment.clientName}</h4>
                {appointment.isNewClient && (
                  <span className="px-2 py-0.5 text-xs font-semibold bg-blue-100 text-blue-700 rounded">
                    NEW
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-600 dark:text-slate-400">Booking #{appointment.bookingNumber}</p>
            </div>
          </div>

          <span
            className={cn(
              'px-3 py-1 text-xs font-semibold rounded-full border',
              config.bg,
              config.text,
              config.border
            )}
          >
            {config.label}
          </span>
        </div>

        {/* Quick Info */}
        <div className="grid grid-cols-2 gap-4 mb-3">
          <div className="flex items-center text-sm text-gray-600 dark:text-slate-400">
            <Calendar className="w-4 h-4 mr-2 text-gray-400 dark:text-slate-500" />
            {dateDisplay}
          </div>
          <div className="flex items-center text-sm text-gray-600 dark:text-slate-400">
            <Clock className="w-4 h-4 mr-2 text-gray-400 dark:text-slate-500" />
            {startTime} - {endTime}
          </div>
        </div>

        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-600 dark:text-slate-400">{appointment.serviceName}</span>
          <span className="font-semibold text-green-600">
            {formatCurrency(appointment.earnings)}
          </span>
        </div>
      </div>

      {/* Expandable Details */}
      <div className="border-t border-gray-100 dark:border-slate-700">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full px-4 py-2 flex items-center justify-center text-sm text-gray-600 dark:text-slate-400 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
        >
          {isExpanded ? (
            <>
              <span>Less Details</span>
              <ChevronUp className="w-4 h-4 ml-1" />
            </>
          ) : (
            <>
              <span>More Details</span>
              <ChevronDown className="w-4 h-4 ml-1" />
            </>
          )}
        </button>

        {isExpanded && (
          <div className="px-4 pb-4 space-y-4">
            {/* Contact Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="flex items-center text-sm text-gray-600 dark:text-slate-400">
                <Mail className="w-4 h-4 mr-2 text-gray-400 dark:text-slate-500" />
                {appointment.clientEmail}
              </div>
              <div className="flex items-center text-sm text-gray-600 dark:text-slate-400">
                <Phone className="w-4 h-4 mr-2 text-gray-400 dark:text-slate-500" />
                {appointment.clientPhone || 'Not provided'}
              </div>
              <div className="flex items-center text-sm text-gray-600 dark:text-slate-400">
                <MapPin className="w-4 h-4 mr-2 text-gray-400 dark:text-slate-500" />
                {appointment.location}
              </div>
              <div className="flex items-center text-sm text-gray-600 dark:text-slate-400">
                <FileText className="w-4 h-4 mr-2 text-gray-400 dark:text-slate-500" />
                {appointment.duration} minutes
              </div>
            </div>

            {/* Special Requests */}
            {appointment.specialRequests && (
              <div className="p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                <p className="text-xs text-yellow-600 font-medium mb-1">Special Requests:</p>
                <p className="text-sm text-yellow-800">{appointment.specialRequests}</p>
              </div>
            )}

            {/* Client Notes */}
            {appointment.clientNotes && (
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-xs text-blue-600 font-medium mb-1">Your Notes About Client:</p>
                <p className="text-sm text-blue-800">{appointment.clientNotes}</p>
              </div>
            )}

            {/* Payment Info */}
            <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-slate-700 rounded-lg">
              <div>
                <p className="text-xs text-gray-500 dark:text-slate-400 mb-1">Your Earnings</p>
                <p className="text-lg font-bold text-green-600">
                  {formatCurrency(appointment.earnings)}
                </p>
              </div>
              <div className="text-right">
                <p className="text-xs text-gray-500 dark:text-slate-400 mb-1">Payment Status</p>
                <span
                  className={cn(
                    'px-2 py-1 text-xs font-medium rounded capitalize',
                    appointment.paymentStatus === 'paid'
                      ? 'bg-green-100 text-green-700'
                      : appointment.paymentStatus === 'failed'
                        ? 'bg-red-100 text-red-700'
                        : 'bg-yellow-100 text-yellow-700'
                  )}
                >
                  {appointment.paymentStatus}
                </span>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end gap-3 pt-2">
              {canUpdateStatus && onUpdateStatus && (
                <>
                  <button
                    onClick={() => onUpdateStatus(appointment.id, 'completed')}
                    className="px-4 py-2 text-sm font-medium text-green-600 border border-green-300 rounded-lg hover:bg-green-50 transition-colors"
                  >
                    Mark Complete
                  </button>
                  <button
                    onClick={() => onUpdateStatus(appointment.id, 'no_show')}
                    className="px-4 py-2 text-sm font-medium text-yellow-600 border border-yellow-300 rounded-lg hover:bg-yellow-50 transition-colors"
                  >
                    No Show
                  </button>
                </>
              )}
              <Link
                to={`/therapist/appointments/${appointment.id}`}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
              >
                View Details
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export const UpcomingBookings: FC<UpcomingBookingsProps> = ({
  appointments,
  isLoading,
  onUpdateStatus,
}) => {
  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (!appointments || appointments.length === 0) {
    return (
      <div className="bg-white dark:bg-slate-800 rounded-lg p-12 text-center border-2 border-dashed border-gray-300 dark:border-slate-600">
        <Calendar className="w-16 h-16 text-gray-400 dark:text-slate-500 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 dark:text-slate-100 mb-2">No Upcoming Bookings</h3>
        <p className="text-gray-600 dark:text-slate-400">You don't have any bookings scheduled for the next 7 days.</p>
      </div>
    );
  }

  // Sort by start time and group by date
  const sortedAppointments = [...appointments].sort((a, b) =>
    a.startTime.localeCompare(b.startTime)
  );

  // Group appointments by date
  const groupedAppointments = sortedAppointments.reduce<Record<string, TherapistAppointment[]>>(
    (groups, appointment) => {
      const date = appointment.date;
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(appointment);
      return groups;
    },
    {}
  );

  return (
    <div className="space-y-6">
      {Object.entries(groupedAppointments).map(([date, dateAppointments]) => (
        <div key={date}>
          <h3 className="text-sm font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wide mb-3">
            {formatDate(date)}
          </h3>
          <div className="space-y-4">
            {dateAppointments.map((appointment) => (
              <BookingCard
                key={appointment.id}
                appointment={appointment}
                onUpdateStatus={onUpdateStatus}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};
